import { Avatar, AvatarFallback, AvatarImage } from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';

import { AnalysisMarkdown, AnalysisSubHeader } from '../../../components';
import type { ExecutiveSnapshotDto } from '@inference.engine/api-client';
import { useTranslation } from 'react-i18next';

import { urlUnfurlQueryOptions } from '@/entities/url-unfurl';
import { hasContent } from '../../../utils/hasContent';

interface CompanyInformationProps {
  data?: ExecutiveSnapshotDto['companyInfo'];
}

/** Whether any field this block renders has content; `employees` and `revenue` are not shown. */
export const hasCompanyInformation = (data: CompanyInformationProps['data']) =>
  hasContent([
    data?.founded,
    data?.headquarters,
    data?.publicPrivate,
    data?.size,
    data?.keyExecutives?.map((leader) => leader.name),
  ]);

export const CompanyInformation = ({ data }: CompanyInformationProps) => {
  const { t } = useTranslation('analysis');

  if (!data || !hasCompanyInformation(data)) return null;

  const details = [
    { label: t('analysisResult.companyInformation.founded'), value: data.founded?.toString() },
    { label: t('analysisResult.companyInformation.headquarters'), value: data.headquarters },
    { label: t('analysisResult.companyInformation.type'), value: data.publicPrivate },
    { label: t('analysisResult.companyInformation.companySize'), value: data.size },
  ].filter((d) => hasContent(d.value));

  const leaders = (data.keyExecutives ?? []).filter((l) => hasContent(l.name));

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.companyInformation.companyInformation')}
        subtitle={t('analysisResult.companyInformation.corporateProfileAndLeadership')}
      />

      {(details.length > 0 || leaders.length > 0) && (
        <div className="grid grid-cols-1 gap-x-xl gap-y-lg rounded-xl border border-border-light p-xl lg:grid-cols-2">
          {details.map((detail) => (
            <DetailItem key={detail.label} label={detail.label} value={detail.value!} />
          ))}

          {/* Leadership shares the box with the detail pairs, spanning its full width. */}
          {leaders.length > 0 && (
            <div className="col-span-full flex flex-col gap-3.5">
              <span className="text-label-md font-medium text-foreground-subtle">
                {t('analysisResult.companyInformation.keyLeadership')}
              </span>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {leaders.map((leader, i) => {
                  const linkedInUrl = toLinkedInUrl(leader.linkedIn);
                  const avatar = <LeaderAvatar name={leader.name} linkedInUrl={linkedInUrl} />;

                  return (
                    <div
                      key={`${leader.name}-${i}`}
                      className="flex min-w-0 flex-row items-center gap-3 rounded-xl p-2"
                    >
                      {linkedInUrl ? (
                        <a
                          href={linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0"
                        >
                          {avatar}
                        </a>
                      ) : (
                        avatar
                      )}
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-heading-sm font-medium text-foreground">
                          {leader.name}
                        </span>
                        {leader.title && (
                          <span className="text-body-sm text-muted-foreground">{leader.title}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** The extractor sometimes writes a placeholder like "LinkedIn available" instead of a URL. */
const toLinkedInUrl = (linkedIn: string | undefined): string | undefined => {
  if (!linkedIn) return undefined;
  try {
    const url = new URL(linkedIn.startsWith('http') ? linkedIn : `https://${linkedIn}`);
    return url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com')
      ? url.toString()
      : undefined;
  } catch {
    return undefined;
  }
};

/** The initials stay underneath: Radix shows them until the photo loads, or when it never does. */
const LeaderAvatar = ({ name, linkedInUrl }: { name: string; linkedInUrl?: string }) => {
  const { data } = useQuery(urlUnfurlQueryOptions(linkedInUrl));
  const photoUrl = data?.resolved ? data.imageUrl : undefined;

  return (
    <Avatar className="size-12">
      {photoUrl && <AvatarImage src={photoUrl} alt={name} referrerPolicy="no-referrer" />}
      <AvatarFallback className="bg-accent text-label-md font-medium text-accent-foreground uppercase">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

/** First-name + last-name initials (e.g. "Carol B. Tomé" → "CT"). */
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`;
};

const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-label-md font-medium text-foreground-subtle">{label}</span>
    <AnalysisMarkdown text={value} className="text-body-lg font-medium text-foreground" />
  </div>
);
